import type {InvalidateQueryFilters, QueryKey} from '@github-ui/react-query'
import {useInvalidateOnAliveUpdate} from '@github-ui/use-invalidate-on-alive-update'

import type {AliveChannels as MergeBoxAliveChannels} from '../page-data/payloads/merge-box'
import type {AliveChannels as StatusChecksAliveChannels} from '../page-data/payloads/status-checks'

// Keys are derived from the real payload types, so adding a channel to either
// payload automatically requires a matching `useAlive` subscription below.
// Values are loosened to nullable because callers build channel objects whose
// individual channels may be absent at runtime.
export type MergeabilityLiveUpdateChannels = {
  [K in keyof MergeBoxAliveChannels]: string | null | undefined
} & {
  [K in keyof StatusChecksAliveChannels]?: string | null | undefined
}

// Stable fallback so consuming hooks aren't handed a fresh object every render.
export const EMPTY_CHANNELS: MergeabilityLiveUpdateChannels = {
  stateChannel: null,
  deployedChannel: null,
  reviewStateChannel: null,
  workflowsChannel: null,
  mergeQueueChannel: null,
  headRefChannel: null,
  baseRefChannel: null,
  gitMergeStateChannel: null,
  pullRequestChannel: null,
  codeScanningChannel: null,
  commitHeadShaChannel: null,
  proofOfPresenceChannel: null,
} as const

export function useMergeabilityLiveUpdates({
  channels,
  queryKey,
  throttleTime = 2000,
}: {
  channels: MergeabilityLiveUpdateChannels | null
  queryKey: QueryKey | InvalidateQueryFilters
  throttleTime?: number
}): void {
  // Subscribe to every channel. Calling useAlive as object-literal values keeps
  // hook order stable (properties evaluate unconditionally, in source order),
  // while `satisfies Record<keyof ...>` makes it a compile error to add a new
  // channel key without also subscribing to it here.
  const invalidateConfig = {throttleTimeout: throttleTime}
  ;({
    stateChannel: useInvalidateOnAliveUpdate(channels?.stateChannel, queryKey, invalidateConfig),
    deployedChannel: useInvalidateOnAliveUpdate(channels?.deployedChannel, queryKey, invalidateConfig),
    reviewStateChannel: useInvalidateOnAliveUpdate(channels?.reviewStateChannel, queryKey, invalidateConfig), // prettier-ignore
    workflowsChannel: useInvalidateOnAliveUpdate(channels?.workflowsChannel, queryKey, invalidateConfig),
    mergeQueueChannel: useInvalidateOnAliveUpdate(channels?.mergeQueueChannel, queryKey, invalidateConfig), // prettier-ignore
    headRefChannel: useInvalidateOnAliveUpdate(channels?.headRefChannel, queryKey, invalidateConfig),
    baseRefChannel: useInvalidateOnAliveUpdate(channels?.baseRefChannel, queryKey, invalidateConfig),
    gitMergeStateChannel: useInvalidateOnAliveUpdate(channels?.gitMergeStateChannel, queryKey, invalidateConfig), // prettier-ignore
    pullRequestChannel: useInvalidateOnAliveUpdate(channels?.pullRequestChannel, queryKey, invalidateConfig), // prettier-ignore
    codeScanningChannel: useInvalidateOnAliveUpdate(channels?.codeScanningChannel, queryKey, invalidateConfig), // prettier-ignore
    commitHeadShaChannel: useInvalidateOnAliveUpdate(channels?.commitHeadShaChannel, queryKey, invalidateConfig), // prettier-ignore
    proofOfPresenceChannel: useInvalidateOnAliveUpdate(channels?.proofOfPresenceChannel, queryKey, invalidateConfig), // prettier-ignore
  }) satisfies Record<keyof MergeabilityLiveUpdateChannels, void>
}
